import React, { useEffect, useState } from "react";
import { Button, Divider, message, Modal, Popover } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { FIRESTORE_ORDERS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { CRATES } from "../../../../../constants/PackagingConstants";
import QuantityControl from "../../../../../components/shared-components/QuantityControl";
import dayjs from "dayjs";
import { updatePackagingStockDelta } from "../../../../../functions/packagingStock";

const EditCratesModal = ({ visible, onCancel, order, orders }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [scannedCrates, setScannedCrates] = useState([]);
  const [addCrateDropdownVisible, setAddCrateDropdownVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const isBulkMode = Array.isArray(orders) && orders.length > 0;

  useEffect(() => {
    if (visible) {
      if (isBulkMode) {
        setScannedCrates([]);
      } else if (order) {
        setScannedCrates(order.scannedCrates || []);
      }
    }
  }, [visible, order, orders]);

  const handleAddCrate = (crateBarcode) => {
    const crate = CRATES.find((c) => c.barcode === crateBarcode);
    if (!crate) return;

    setScannedCrates((prev) => {
      const existing = prev.find((c) => c.barcode === crateBarcode);
      if (existing) {
        return prev.map((c) => (c.barcode === crateBarcode ? { ...c, quantity: c.quantity + 1 } : c));
      }
      return [...prev, { barcode: crateBarcode, quantity: 1, size: crate.size }];
    });
  };

  const handleSubmit = async () => {
    if (!isBulkMode && !order) return;

    try {
      setSubmitLoading(true);

      if (isBulkMode) {
        await Promise.all(
          orders.map((o) =>
            updateDoc(doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, o.id), {
              scannedCrates: scannedCrates || [],
              updatedAt: dayjs().unix(),
              updatedBy: email,
            }),
          ),
        );

        // Combine each order's old/new crates into one delta per barcode first, then apply
        // via a single transaction per barcode. Running one transaction per order in parallel
        // (the previous approach) fires N concurrent transactions at the same packagingStocks
        // document, which causes Firestore version-conflict failures under contention and
        // silently drops updates.
        const combinedOld = orders.flatMap((o) => o.scannedCrates || []);
        const combinedNew = orders.flatMap(() => scannedCrates || []);
        try {
          const stockResult = await updatePackagingStockDelta(combinedOld, combinedNew, email);
          if (!stockResult.success) {
            console.error("Failed to update packaging stock:", stockResult.error);
          }
        } catch (error) {
          console.error("Error updating packaging stock:", error);
        }

        message.success(t("notifications.bulk_crates_updated_successfully", { count: orders.length }));
      } else {
        await updateDoc(
          doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, order.id),
          {
            scannedCrates: scannedCrates || [],
            updatedAt: dayjs().unix(),
            updatedBy: email,
          },
        );

        try {
          const stockResult = await updatePackagingStockDelta(order.scannedCrates || [], scannedCrates || [], email);
          if (!stockResult.success) {
            console.error("Failed to update packaging stock:", stockResult.error);
          }
        } catch (error) {
          console.error("Error updating packaging stock:", error);
        }

        message.success(t("notifications.crates_updated_successfully"));
      }

      onCancel();
    } catch (error) {
      console.error("Error updating crates:", error);
      message.error(t("notifications.error_updating_crates"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleCancel = () => {
    setScannedCrates([]);
    setAddCrateDropdownVisible(false);
    onCancel();
  };

  return (
    <Modal
      title={isBulkMode ? t("bulk_edit_crates_title", { count: orders.length }) : t("edit_crates")}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={submitLoading}
      okText={t("save")}
      cancelText={t("cancel")}
      width={500}
    >
      <div style={{ marginBottom: "16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h4 style={{ margin: 0 }}>{t("crates")}</h4>
          <div>
            <Popover
              content={
                <div style={{ width: "200px" }}>
                  {CRATES.map((crate, index) => (
                    <div
                      key={crate.barcode}
                      onClick={() => {
                        handleAddCrate(crate.barcode);
                        setAddCrateDropdownVisible(false);
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderRadius: "4px",
                        transition: "background-color 0.2s",
                        borderBottom: index < CRATES.length - 1 ? "1px solid #f0f0f0" : "none",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f5f5f5";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {crate.name}
                    </div>
                  ))}
                </div>
              }
              trigger="click"
              open={addCrateDropdownVisible}
              onOpenChange={(visible) => {
                setAddCrateDropdownVisible(visible);
              }}
            >
              <Button type="text" icon={<PlusOutlined />} size="small">
                {t("add")}
              </Button>
            </Popover>
          </div>
        </div>

        <Divider style={{ margin: "12px 0" }} />

        <div>
          {scannedCrates.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>{t("no_crates_added")}</div>
          ) : (
            scannedCrates.map((scannedCrate, index) => {
              const crate = CRATES.find((c) => c.barcode === scannedCrate.barcode);
              const isLast = index === scannedCrates.length - 1;
              return (
                <div
                  key={scannedCrate.barcode}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 0",
                    borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                  }}
                >
                  <span>{crate.name}</span>
                  <QuantityControl
                    productId={scannedCrate.barcode}
                    currentQuantity={scannedCrate.quantity}
                    onIncrease={() => {
                      setScannedCrates((prev) =>
                        prev.map((c) => (c.barcode === scannedCrate.barcode ? { ...c, quantity: c.quantity + 1 } : c)),
                      );
                    }}
                    onDecrease={() => {
                      if (scannedCrate.quantity > 0) {
                        setScannedCrates((prev) => {
                          const updated = prev.map((c) =>
                            c.barcode === scannedCrate.barcode ? { ...c, quantity: Math.max(0, c.quantity - 1) } : c,
                          );
                          return updated.filter((c) => c.quantity > 0);
                        });
                      }
                    }}
                    onManualInput={(id, value) => {
                      setScannedCrates((prev) => {
                        const updated = prev.map((c) =>
                          c.barcode === scannedCrate.barcode ? { ...c, quantity: Math.max(0, value) } : c,
                        );
                        return updated.filter((c) => c.quantity > 0);
                      });
                    }}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};

export default EditCratesModal;
